import React, {useEffect, useMemo, useState} from 'react'
import {CardDefinitionVariables} from '../../../common/types'
import {
  Comparison,
  DynamicVariableSwitch,
  EventOverrideConfig,
  ListVariable,
  ListVariableValueCodec,
  SimpleVariable,
  Value
} from '../../../common/types/variable-engine'
import {BetaFlagMarker} from '../../../components/feature-flags'
import {ButtonGroup} from '../../../components/form'
import {Button} from '../../../components/library/buttons'
import {
  IconButton,
  LinkIconButton
} from '../../../components/library/buttons/icon-button'
import {DropdownSelect} from '../../../components/library/dropdown/select'
import {InputGroup, Select, TextInput} from '../../../components/library/form'
import {DatePickerInput} from '../../../components/library/form/date-input'
import {JSONField} from '../../../components/library/json-field'
import {Modal} from '../../../components/library/modal/modal'
import styled from 'styled-components'
import {TextEditor} from '../../../components/library/text-editor'
import {Switch} from '../../../components/library/toggles/switch'
import {Tooltip} from '../../../components/library/tooltip/tooltip'
import {toTitleCase} from '../../../lib/helpers'
import {VariableAction} from './actions'
import {
  getFlatValue,
  onAllowEventOverrideChange,
  onRequiredChange,
  storeDefaultInSession
} from './helpers'
import {FormState, NonSwitchVariableType} from './variable-state'

import type {VariablePath} from '../../../components/library/text-editor/helpers'
import {useFormatPreview} from '../../../hooks/use-date-format-preview'

const ConditionPill = styled.div`
  display: inline-flex;
  align-items: center;
  box-shadow: 0 0 0 1px ${p => p.theme.grey10};
  border-radius: ${p => p.theme.borderRadiusPill};
  height: 30px;
  padding: 5px 15px;
  font-size: ${p => p.theme.fontSizeXS};
  line-height: ${p => p.theme.lineHeightCompact};
  color: ${p => p.theme.grey50};
  position: relative;

  &:after {
    width: 2px;
    height: 10px;
    left: 20px;
    top: 100%;
    content: '';
    position: absolute;
    background: ${p => p.theme.grey10};
  }

  .static {
    color: ${p => p.theme.black};
    margin-left: 2px;
    margin-right: 2px;
  }

  .variable {
    color: ${p => p.theme.blue};
    margin-left: 2px;
    margin-right: 2px;
  }
`

const ConditionSection = styled.div``

const Condition = styled.div`
  ${ConditionSection}:nth-child(n + 2) {
    padding-top: 10px;
    ${ConditionPill} {
      &:before {
        width: 2px;
        height: 10px;
        left: 20px;
        top: -10px;
        content: '';
        position: absolute;
        background: ${p => p.theme.grey10};
      }
    }
  }
`

const Link = styled.div`
  height: 20px;
  position: relative;
  &:before {
    width: 2px;
    height: 20px;
    left: 50%;
    top: 0;
    transform: translateX(1px);
    content: '';
    position: absolute;
    background: ${p => p.theme.grey10};
  }
`

const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`

const OtherwiseRow = styled.div`
  display: flex;
  margin-top: -10px;
  margin-bottom: -10px;
  ${ConditionPill} {
    &:before {
      width: 2px;
      height: 10px;
      left: 20px;
      top: -10px;
      content: '';
      position: absolute;
      background: ${p => p.theme.grey10};
    }
  }
`

const AdditionalConditionButton = styled.div`
  margin-top: -20px;
  button {
    display: inline-flex;
    align-items: center;
    border-radius: ${p => p.theme.borderRadiusPill};
    height: 30px;
    padding: 5px 15px;
    font-size: ${p => p.theme.fontSizeXS};
    line-height: ${p => p.theme.lineHeightCompact};
    font-weight: bold;
    position: relative;
  }
`

const TopConnector = styled.div`
  width: 2px;
  height: 10px;
  margin-left: 20px;
  background: ${p => p.theme.grey10};
`

const SwitchLabelDescription = styled.div`
  margin-top: 5px;
  color: ${p => p.theme.grey50};
  font-size: ${p => p.theme.fontSizeS};
  line-height: ${p => p.theme.lineHeightCompact};
`

const SwitchLabelTitle = styled.div``

const SwitchLabelWrap = styled.div``

//-------------BasicVariableTypeSelector-------------
type BasicVariableTypeSelectorProps = {
  type?: NonSwitchVariableType
  onChange: (varType: NonSwitchVariableType) => void
  disabled?: boolean
  readOnly?: boolean
  showScriptVariables?: boolean
  displayingInActionFlow?: boolean
}

export const BasicVariableTypeSelector = ({
  type,
  onChange,
  disabled,
  readOnly,
  showScriptVariables = true
}: BasicVariableTypeSelectorProps) => {
  const nonSwitchVars: NonSwitchVariableType[] = [
    'string',
    'number',
    'date',
    'boolean',
    'list',

    ...(showScriptVariables ? ['script' as const] : [])
  ]

  return (
    <Select<NonSwitchVariableType>
      label="Select variable type..."
      upLabel="Type"
      options={nonSwitchVars.map(t => ({
        text: t === 'script' ? 'Script (beta)' : toTitleCase(String(t)),
        value: t
      }))}
      value={type}
      onChange={v => v && onChange(v)}
      disabled={disabled}
      readOnly={readOnly}
    />
  )
}

// ------------AllowEventOverwrite Toggle------------
type AllowEventOverwriteProps = {
  eventOverride?: EventOverrideConfig
  onChange: (e: EventOverrideConfig) => void
  fieldName?: string
  readOnly?: boolean
}
export const AllowEventOverwrite = ({
  eventOverride,
  fieldName,
  onChange,
  readOnly
}: AllowEventOverwriteProps) => {
  const allowEventOverwriteChecked = !eventOverride?.disabled
  return (
    <>
      <Switch
        isForm={true}
        label={
          <SwitchLabelWrap>
            <SwitchLabelTitle>
              Allow request payload to overwrite
            </SwitchLabelTitle>
            <SwitchLabelDescription>
              {allowEventOverwriteChecked &&
                `This value will be overwritten if the payload you send us contains a value for ${
                  fieldName ? `'${fieldName}'` : 'this variable'
                }`}
              {!allowEventOverwriteChecked &&
                'The value will not be affected by any payload properties'}
            </SwitchLabelDescription>
          </SwitchLabelWrap>
        }
        labelPosition="left"
        checked={allowEventOverwriteChecked}
        onChange={checked => {
          onChange({
            disabled: !checked,
            required: eventOverride?.required
          })
        }}
        readOnly={readOnly}
      />
    </>
  )
}

type EventOverwriteRequiredProps = {
  required?: boolean
  onChange: (v: boolean) => void
  fieldName?: string
  readOnly?: boolean
}
export const EventOverwriteRequired = ({
  required,
  fieldName,
  onChange,
  readOnly
}: EventOverwriteRequiredProps) => {
  return (
    <>
      <Switch
        isForm={true}
        label={
          <SwitchLabelWrap>
            <SwitchLabelTitle>
              Value required
              <BetaFlagMarker />
            </SwitchLabelTitle>
            <SwitchLabelDescription>
              A value for {fieldName ? `'${fieldName}'` : 'this variable'} must
              be provided in the payload. If none is provided the request to
              create the card will fail
            </SwitchLabelDescription>
          </SwitchLabelWrap>
        }
        labelPosition="left"
        checked={!!required}
        onChange={checked => onChange(checked)}
        readOnly={readOnly}
      />
    </>
  )
}

// ------------Runtime Toggle-------------
type RuntimeToggleProps = {
  checked?: boolean
  onChange: (checked: boolean) => void
  readOnly?: boolean
}
export const RuntimeToggle = ({
  checked = false,
  onChange,
  readOnly
}: RuntimeToggleProps) => (
  <Switch
    isForm={true}
    label={
      <SwitchLabelWrap>
        <SwitchLabelTitle>Allow update at runtime</SwitchLabelTitle>
        <SwitchLabelDescription>
          {checked
            ? 'Value can be changed at runtime'
            : 'Value cannot be changed at runtime'}
        </SwitchLabelDescription>
      </SwitchLabelWrap>
    }
    labelPosition="left"
    checked={checked}
    onChange={v => onChange(v)}
    readOnly={readOnly}
  />
)

// ------------NonWrappingString Toggle------------
type NonWrappingStringProps = {
  checked: boolean
  onChange: (checked: boolean) => void
  readOnly?: boolean
}

export const NonWrappingString = ({
  checked,
  onChange,
  readOnly
}: NonWrappingStringProps) => (
  <>
    <Switch
      isForm={true}
      label={
        <SwitchLabelWrap>
          <SwitchLabelTitle>Non-wrapping string</SwitchLabelTitle>
          <SwitchLabelDescription>
            {checked
              ? 'Variable will always be displayed on a single line of text'
              : 'Variable may be split over several lines of text'}
          </SwitchLabelDescription>
        </SwitchLabelWrap>
      }
      labelPosition="left"
      checked={checked}
      onChange={onChange}
      readOnly={readOnly}
    />
  </>
)

type BooleanFormProps = {
  activeVariable: SimpleVariable
  dispatch: React.Dispatch<VariableAction>
  varName: string
  readOnly?: boolean
  providerId?: string
  mode: FormState['mode']
}
export const BooleanForm = ({
  varName,
  activeVariable,
  dispatch,
  readOnly,
  providerId,
  mode
}: BooleanFormProps) => {
  const flatValue = getFlatValue(activeVariable.default)
  return (
    <>
      {(activeVariable.eventOverride?.disabled ||
        !activeVariable.eventOverride?.required) && (
        <Switch
          isForm={true}
          label={`Value is set to ${flatValue ? 'true' : 'false'}`}
          labelPosition="left"
          checked={!!flatValue}
          onChange={v => {
            storeDefaultInSession('boolean', varName, v, providerId)
            dispatch({type: 'setDefaultValue', payload: v})
          }}
          readOnly={readOnly}
        />
      )}
      <InputGroup>
        <AllowEventOverwrite
          eventOverride={activeVariable.eventOverride}
          onChange={eventOverride =>
            onAllowEventOverrideChange({
              variableType: 'boolean',
              variableName: varName,
              currentEventOverride: activeVariable.eventOverride,
              currentDefaultValue: !!flatValue,
              newEventOverride: eventOverride,
              providerId,
              dispatch
            })
          }
          fieldName={varName}
          readOnly={readOnly}
        />
        {mode !== 'user-profile' && !activeVariable.eventOverride?.disabled && (
          <EventOverwriteRequired
            fieldName={varName}
            required={activeVariable.eventOverride?.required}
            onChange={checked =>
              onRequiredChange({
                variableType: 'boolean',
                variableName: varName,
                currentDefaultValue: !!flatValue,
                newRequiredValue: checked,
                providerId,
                dispatch
              })
            }
            readOnly={readOnly}
          />
        )}
      </InputGroup>
    </>
  )
}

// ------------------NumberForm---------------------
type NumberFormProps = {
  varName: string
  mode: FormState['mode']
  activeVariable: SimpleVariable
  dispatch: React.Dispatch<VariableAction>
  readOnly?: boolean
  providerId?: string
}
export const NumberForm = ({
  varName,
  activeVariable,
  dispatch,
  readOnly,
  providerId,
  mode
}: NumberFormProps) => {
  const flatValue = getFlatValue(activeVariable.default)
  const stringValue = flatValue ? String(flatValue) : ''

  return (
    <>
      {(activeVariable.eventOverride?.disabled ||
        !activeVariable.eventOverride?.required) && (
        <TextInput
          label={'Give it a value...'}
          upLabel={'Value'}
          value={stringValue}
          onChange={v => {
            storeDefaultInSession('number', varName, v, providerId)
            dispatch({
              type: 'setDefaultValue',
              payload: v
            })
          }}
          readOnly={readOnly}
        />
      )}
      <InputGroup>
        <AllowEventOverwrite
          eventOverride={activeVariable.eventOverride}
          onChange={eventOverride =>
            onAllowEventOverrideChange({
              variableType: 'number',
              variableName: varName,
              currentEventOverride: activeVariable.eventOverride,
              currentDefaultValue: stringValue,
              newEventOverride: eventOverride,
              providerId,
              dispatch
            })
          }
          fieldName={varName}
          readOnly={readOnly}
        />
        {mode !== 'user-profile' && !activeVariable.eventOverride?.disabled && (
          <EventOverwriteRequired
            fieldName={varName}
            required={activeVariable.eventOverride?.required}
            onChange={checked =>
              onRequiredChange({
                variableType: 'number',
                variableName: varName,
                currentDefaultValue: stringValue,
                newRequiredValue: checked,
                providerId,
                dispatch
              })
            }
            readOnly={readOnly}
          />
        )}
      </InputGroup>
    </>
  )
}

// ---------------- ListForm ------------
type ListFormProps = {
  activeVariable: ListVariable
  dispatch: React.Dispatch<VariableAction>
  variablePaths: VariablePath[]
  varName: string
  readOnly?: boolean
  providerId?: string
  mode: FormState['mode']
}

export const ListForm = ({
  varName,
  dispatch,
  activeVariable,
  readOnly,
  providerId,
  mode
}: ListFormProps) => {
  const flatValue = getFlatValue(activeVariable.default) ?? []
  return (
    <>
      {(activeVariable.eventOverride?.disabled ||
        !activeVariable.eventOverride?.required) && (
        <JSONField
          codec={ListVariableValueCodec}
          label={'Value'}
          value={flatValue}
          onChange={value => {
            storeDefaultInSession('list', varName, value, providerId)
            dispatch({type: 'setListDefault', payload: value})
          }}
          style={{height: '100px'}}
          raw={true}
          readOnly={readOnly}
          legacyMode={true}
        />
      )}

      <InputGroup>
        <AllowEventOverwrite
          eventOverride={activeVariable.eventOverride}
          onChange={eventOverride =>
            onAllowEventOverrideChange({
              variableType: 'list',
              variableName: varName,
              currentEventOverride: activeVariable.eventOverride,
              currentDefaultValue: flatValue,
              newEventOverride: eventOverride,
              providerId,
              dispatch
            })
          }
          fieldName={varName}
          readOnly={readOnly}
        />
        {mode !== 'user-profile' && !activeVariable.eventOverride?.disabled && (
          <EventOverwriteRequired
            fieldName={varName}
            required={activeVariable.eventOverride?.required}
            onChange={checked =>
              onRequiredChange({
                variableType: 'list',
                variableName: varName,
                currentDefaultValue: flatValue,
                newRequiredValue: checked,
                providerId,
                dispatch
              })
            }
            readOnly={readOnly}
          />
        )}
      </InputGroup>
    </>
  )
}

type StringFormProps = {
  activeVariable: SimpleVariable
  dispatch: React.Dispatch<VariableAction>
  variablePaths: VariablePath[]
  mode: FormState['mode']
  varName: string
  readOnly?: boolean
  providerId?: string
}

export const StringForm = ({
  dispatch,
  activeVariable,
  variablePaths,
  mode,
  varName,
  readOnly,
  providerId
}: StringFormProps) => {
  const flatValue = getFlatValue(activeVariable.default)
  const stringValue = flatValue ? String(flatValue) : ''
  return (
    <>
      {(activeVariable.eventOverride?.disabled ||
        !activeVariable.eventOverride?.required) && (
        <InputGroup>
          <TextEditor
            label={
              mode === 'custom'
                ? 'Give it a value...'
                : 'Give it a fallback value...'
            }
            upLabel={mode === 'custom' ? 'Value' : 'Fallback value'}
            value={stringValue}
            addNewVariables={false}
            onChange={v => {
              storeDefaultInSession('string', varName, v, providerId)
              dispatch({type: 'setDefaultValue', payload: v})
            }}
            variablePaths={variablePaths}
            readOnly={readOnly}
            legacyMode={true}
          />

          {mode !== 'user-profile' && !readOnly && (
            <Button
              appearance="tint"
              stretch="center"
              size="large"
              iconLeft="add"
              onClick={() =>
                dispatch({
                  type: 'addCondition'
                })
              }
            >
              Add condition
            </Button>
          )}
        </InputGroup>
      )}

      <NonWrappingString
        checked={activeVariable.defaultFormatter === 'nonWrapping'}
        onChange={v => {
          dispatch({
            type: 'setNonWrappingFormatter',
            payload: {formatter: v ? 'nonWrapping' : undefined}
          })
        }}
        readOnly={readOnly}
      />
      <InputGroup>
        <AllowEventOverwrite
          eventOverride={activeVariable.eventOverride}
          onChange={eventOverride =>
            onAllowEventOverrideChange({
              variableType: 'string',
              variableName: varName,
              currentEventOverride: activeVariable.eventOverride,
              currentDefaultValue: stringValue,
              newEventOverride: eventOverride,
              providerId,
              dispatch
            })
          }
          fieldName={varName}
          readOnly={readOnly}
        />

        {mode !== 'user-profile' && !activeVariable.eventOverride?.disabled && (
          <EventOverwriteRequired
            fieldName={varName}
            required={activeVariable.eventOverride?.required}
            onChange={checked =>
              onRequiredChange({
                variableType: 'string',
                variableName: varName,
                currentDefaultValue: stringValue,
                newRequiredValue: checked,
                providerId,
                dispatch
              })
            }
            readOnly={readOnly}
          />
        )}
      </InputGroup>
      <RuntimeToggle
        checked={activeVariable.runtime}
        onChange={runtime => dispatch({type: 'setRuntime', payload: runtime})}
        readOnly={readOnly}
      />
    </>
  )
}

//-------------------------DateForm-------------------

type DateFormProps = {
  activeVariable: SimpleVariable
  dispatch: React.Dispatch<VariableAction>
  varName: string
  mode: FormState['mode']
  readOnly?: boolean
  providerId?: string
}
export const DateForm = ({
  varName,
  mode,
  activeVariable,
  dispatch,
  readOnly,
  providerId
}: DateFormProps) => {
  const flatValue = getFlatValue(activeVariable.default)
  const stringValue = flatValue ? String(flatValue) : ''
  const {formatted: formatPreview} = useFormatPreview(
    stringValue,
    activeVariable.defaultFormatterOption
  )

  return (
    <>
      <InputGroup>
        {(activeVariable.eventOverride?.disabled ||
          !activeVariable.eventOverride?.required) && (
          <DatePickerInput
            label={
              mode === 'custom'
                ? 'Give it a value...'
                : 'Give it a fallback value...'
            }
            upLabel={mode === 'custom' ? 'Value' : 'Fallback value'}
            value={stringValue}
            showTimeSelect={true}
            onChange={v => {
              storeDefaultInSession(
                'date',
                varName,
                v.toISOString(),
                providerId
              )
              dispatch({type: 'setDefaultValue', payload: v.toISOString()})
            }}
            iconRight="date"
            readOnly={readOnly}
          />
        )}

        <TextInput
          buttonRight={
            <Tooltip
              isLight={false}
              position="left"
              content="View date format reference"
            >
              <LinkIconButton
                size="large"
                variant="color"
                to="https://date-fns.org/docs/format"
                target="_blank"
                rel="noopener noreferrer"
                icon="info"
              ></LinkIconButton>
            </Tooltip>
          }
          label="Specify format..."
          upLabel={`Format ${formatPreview ? `– E.g. ${formatPreview}` : ''}`}
          value={activeVariable.defaultFormatterOption || ''}
          onChange={v => {
            dispatch({
              type: 'setDateFormatter',
              payload: {
                formatter: v ? 'date' : undefined,
                formatterOption: v ? v : undefined
              }
            })
          }}
          readOnly={readOnly}
        />
      </InputGroup>
      <InputGroup>
        <AllowEventOverwrite
          eventOverride={activeVariable.eventOverride}
          onChange={eventOverride =>
            onAllowEventOverrideChange({
              variableType: 'date',
              variableName: varName,
              currentEventOverride: activeVariable.eventOverride,
              currentDefaultValue: stringValue,
              newEventOverride: eventOverride,
              providerId,
              dispatch
            })
          }
          fieldName={varName}
          readOnly={readOnly}
        />

        {mode !== 'user-profile' && !activeVariable.eventOverride?.disabled && (
          <EventOverwriteRequired
            fieldName={varName}
            required={activeVariable.eventOverride?.required}
            onChange={checked =>
              onRequiredChange({
                variableType: 'date',
                variableName: varName,
                currentDefaultValue: stringValue,
                newRequiredValue: checked,
                providerId,
                dispatch
              })
            }
            readOnly={readOnly}
          />
        )}
      </InputGroup>
    </>
  )
}

//------------------ConditionForm----------------
type SwitchFormProps = {
  state: FormState
  activeVariable: DynamicVariableSwitch
  dispatch: React.Dispatch<VariableAction>
  variablePaths: VariablePath[]
  variables?: CardDefinitionVariables
  setOrder: (order: 'up' | 'down' | undefined) => void
  name: string
  readOnly?: boolean
}

export const SwitchForm = ({
  state,
  activeVariable,
  dispatch,
  variablePaths,
  setOrder,
  variables,
  name,
  readOnly
}: SwitchFormProps) => {
  const activeSwitchCase =
    state.conditionIndex !== undefined &&
    state.conditionIndex in state.switch.statement.cases
      ? state.switch.statement.cases[state.conditionIndex]
      : undefined

  useEffect(() => {
    setOrder(activeSwitchCase ? 'down' : 'up')
  }, [activeSwitchCase, setOrder])

  const variableOptions = useMemo(
    () =>
      variables
        ? Object.keys(variables)
            // exclude self
            .filter(v => v !== name)
            .map(v => ({text: v, value: v}))
        : [],
    [variables, name]
  )

  const flatValue = getFlatValue(activeVariable.default)
  const stringValue = flatValue ? String(flatValue) : ''
  return (
    <>
      <SwitchConditions
        variable={activeVariable}
        variablePaths={variablePaths}
        onEdit={idx => dispatch({type: 'setConditionIndex', payload: idx})}
        onChangeExpression={(index, expression) => {
          const statementCase = activeVariable.statement.cases[index]
          dispatch({
            type: 'setStatementCase',
            payload: {
              index: index,
              statementCase: {
                ...statementCase,
                expression
              }
            }
          })
        }}
        readOnly={readOnly}
      />
      <OtherwiseRow>
        <ConditionPill>otherwise</ConditionPill>
      </OtherwiseRow>

      <InputGroup>
        <TextEditor
          label={'Give it a value...'}
          upLabel={'Value'}
          value={stringValue}
          addNewVariables={false}
          onChange={v => {
            dispatch({type: 'setDefaultValue', payload: v})
          }}
          variablePaths={variablePaths}
          readOnly={readOnly}
          legacyMode={true}
        />
      </InputGroup>

      {!readOnly && (
        <AdditionalConditionButton>
          <TopConnector />
          <Button
            appearance="secondary"
            onClick={() =>
              dispatch({
                type: 'addCondition'
              })
            }
          >
            + Add another condition
          </Button>
        </AdditionalConditionButton>
      )}
      <NonWrappingString
        checked={activeVariable.defaultFormatter === 'nonWrapping'}
        onChange={v => {
          dispatch({
            type: 'setNonWrappingFormatter',
            payload: {formatter: v ? 'nonWrapping' : undefined}
          })
        }}
        readOnly={readOnly}
      />
      <RuntimeToggle
        checked={activeVariable.runtime}
        onChange={runtime => dispatch({type: 'setRuntime', payload: runtime})}
        readOnly={readOnly}
      />
      {/* Modal */}
      {activeSwitchCase && !readOnly && (
        <Modal
          title={state.newCondition ? 'New condition' : 'Edit condition'}
          handleClose={() => {
            if (state.newCondition) {
              dispatch({type: 'deleteCondition'})
            } else {
              dispatch({
                type: 'setConditionIndex',
                payload: undefined
              })
            }
          }}
          stopPropagation={true}
        >
          <ComparisonForm
            isNew={state.newCondition}
            comparison={activeSwitchCase.comparison}
            variableOptions={variableOptions}
            onSave={comparison =>
              dispatch({
                type: 'setStatementCase',
                payload: {
                  index: state.conditionIndex || 0,
                  statementCase: {
                    ...activeSwitchCase,
                    comparison
                  }
                }
              })
            }
            onCancel={() => {
              if (state.newCondition) {
                dispatch({type: 'deleteCondition'})
              } else {
                dispatch({
                  type: 'setConditionIndex',
                  payload: undefined
                })
              }
            }}
            onDelete={
              state.newCondition
                ? undefined
                : () => dispatch({type: 'deleteCondition'})
            }
          />
        </Modal>
      )}
    </>
  )
}

type ConditionOption = {value: Value['type']; text: string}

const conditionOptions: ConditionOption[] = [
  {value: 'static', text: 'String'},
  {value: 'variable', text: 'Variable'}
]

const VariableSelect = ({
  value,
  variables,
  onChange,
  right
}: {
  value: Value
  variables: {value: string; text: string}[]
  onChange: (v: Value) => void
  right?: boolean
}) => {
  const options =
    variables.length > 0
      ? conditionOptions
      : conditionOptions.filter(o => o.value !== 'variable')

  return (
    <InputGroup inline={true} style={{width: '100%'}}>
      <DropdownSelect<Value['type']>
        blue={true}
        minWidth={340}
        matchContent={true}
        inline={true}
        label={right ? 'This...' : 'If this...'}
        order="up"
        getDisplayValue={v => {
          if (v === 'variable') {
            return right ? 'This variable' : 'If this variable'
          }
          if (v === 'static') {
            return right ? 'This string' : 'If this string'
          }
          return undefined
        }}
        options={options}
        value={value.type}
        onChange={type => {
          onChange(
            type === 'variable'
              ? {
                  type: 'variable',
                  variable: value.type === 'variable' ? value.variable : ''
                }
              : {
                  type: 'static',
                  value: value.type === 'variable' ? '' : value.value
                }
          )
        }}
      />
      {value.type === 'variable' && (
        <DropdownSelect
          style={{flex: 1}}
          inline={true}
          order="up"
          minWidth={undefined}
          label="Select variable..."
          options={variables}
          value={value.variable}
          onChange={(v: string) => {
            if (v) {
              onChange({type: 'variable', variable: v})
            }
          }}
        />
      )}
      {value.type === 'static' && (
        <TextInput
          inline={true}
          label="Enter value..."
          value={value.value || ''}
          onChange={v => {
            onChange({type: 'static', value: v})
          }}
        />
      )}
    </InputGroup>
  )
}

type ComparisonType = Comparison['type']

const comparisonOptions: {value: ComparisonType; text: string}[] = [
  {
    value: 'isTruthy',
    text: 'Is true'
  },
  {
    value: 'isFalsy',
    text: 'Is false'
  },
  {
    value: 'equals',
    text: 'Equals'
  },
  {
    value: 'notEqual',
    text: "Doesn't equal"
  },
  {
    value: 'greaterThan',
    text: 'Is greater than'
  },
  {
    value: 'lessThan',
    text: 'Is less than'
  },
  {
    value: 'greaterThanOrEqualTo',
    text: 'Is greater than or equal to'
  },
  {
    value: 'lessThanOrEqualTo',
    text: 'Is less than or equal to'
  },
  {
    value: 'contains',
    text: 'Contains'
  },
  {
    value: 'notContain',
    text: "Doesn't contain"
  }
]

const getLeftValue = (comparison: Comparison): Value => {
  return 'left' in comparison ? comparison.left : comparison.value
}

const getRightValue = (comparison: Comparison): Value => {
  return 'right' in comparison ? comparison.right : {type: 'static', value: ''}
}

const SwitchConditions = ({
  variable,
  onEdit,
  onChangeExpression,
  variablePaths,
  readOnly
}: {
  variable: DynamicVariableSwitch
  onEdit: (index: number) => void
  onChangeExpression: (index: number, expression: string) => void
  variablePaths: string[]
  readOnly?: boolean
}) => {
  const getStatementConditionType = (type: Comparison['type']) =>
    comparisonOptions.find(o => o.value === type)?.text.toLowerCase() ?? type
  return (
    <Condition>
      {variable.statement.cases.map((statementCase, i) => {
        const left = getLeftValue(statementCase.comparison)
        const right = getRightValue(statementCase.comparison)
        return (
          <ConditionSection key={`statementCase${i}`}>
            <ConditionRow>
              <ConditionPill>
                if
                <span className={left.type}>
                  {left.type === 'variable' ? left.variable : left.value}
                </span>
                {getStatementConditionType(statementCase.comparison.type)}
                {statementCase.comparison.type !== 'isTruthy' &&
                  statementCase.comparison.type !== 'isFalsy' && (
                    <span className={right.type}>
                      {right.type === 'variable' ? right.variable : right.value}
                    </span>
                  )}
              </ConditionPill>
              {!readOnly && <EditButton onEdit={onEdit} index={i} />}
            </ConditionRow>
            <TextEditor
              label="Enter value..."
              upLabel="Value"
              value={statementCase.expression}
              onChange={value => onChangeExpression(i, value)}
              variablePaths={variablePaths}
              readOnly={readOnly}
              legacyMode={true}
            />
          </ConditionSection>
        )
      })}
    </Condition>
  )
}

const EditButton = ({
  onEdit,
  index
}: {
  onEdit: (index: number) => void
  index: number
}) => {
  return (
    <Button
      appearance="transparent"
      size="xsmall"
      iconLeft="edit_small"
      onClick={() => onEdit(index)}
    >
      Edit
    </Button>
  )
}

const ComparisonForm = ({
  comparison,
  variableOptions,
  onSave,
  onCancel,
  onDelete,
  isNew
}: {
  comparison: Comparison
  variableOptions: {text: string; value: string}[]
  onSave: (comparison: Comparison) => void
  onCancel: () => void
  onDelete?: () => void
  isNew: boolean
}) => {
  const [localState, setLocalState] = useState(comparison)
  const leftValue = getLeftValue(localState)
  const rightValue = getRightValue(localState)

  return (
    <div style={{maxWidth: '340px'}}>
      <VariableSelect
        variables={variableOptions}
        value={leftValue}
        onChange={left => {
          setLocalState(v => ({...v, left}))
        }}
      />
      <Link />
      <Select<ComparisonType>
        label="Select condition..."
        inline={true}
        options={comparisonOptions}
        value={localState.type}
        onChange={v => {
          if (!v) return
          setLocalState(
            v === 'isTruthy' || v === 'isFalsy'
              ? {
                  type: v,
                  value: leftValue
                }
              : {
                  type: v,
                  left: leftValue,
                  right:
                    'right' in localState
                      ? localState.right
                      : {type: 'static', value: ''}
                }
          )
        }}
      />

      {localState.type !== 'isTruthy' && localState.type !== 'isFalsy' && (
        <>
          <Link />
          <VariableSelect
            right={true}
            variables={variableOptions}
            value={rightValue}
            onChange={right => {
              setLocalState(v => ({...v, right}))
            }}
          />
        </>
      )}
      <ButtonGroup>
        <Button size="large" onClick={() => onSave(localState)}>
          {isNew ? <>Add condition</> : 'Save'}
        </Button>
        <Button appearance="tint" size="large" onClick={onCancel}>
          Cancel
        </Button>
        {onDelete && (
          <div style={{marginRight: 'auto', marginLeft: '0px'}}>
            <IconButton
              icon="remove"
              onClick={onDelete}
              size="large"
              variant="color"
            />
          </div>
        )}
      </ButtonGroup>
    </div>
  )
}
